class VideoImageCardController {
  constructor() {
    this.breakpoint = 1200;
    this.init();
  }

  static initialize() {
    const controller = new VideoImageCardController();
    return controller;
  }

  init() {
    this.setupVideoControls();
    this.handleResize();
    window.addEventListener( 'resize', () => this.handleResize() );
  }

  setupVideoControls() {
    const videoContainerWrappers = document.querySelectorAll( '.e-div--content-container > .e-div > .e-div' );
    
    videoContainerWrappers.forEach( wrapper => {
      const videoContainer = wrapper.querySelector( '.e-vid' );
      if ( !videoContainer ) return;
      
      const video = videoContainer.querySelector( 'video' );
      const iframe = videoContainer.querySelector( 'iframe' );
      
      if ( video ) {
        this.setupNativeVideo( video, wrapper );
      } else if ( iframe ) {
        this.setupIframeVideo( iframe, wrapper );
      }
    } );
  }

  setupNativeVideo( video, container ) {
    video.muted = true;
    video.setAttribute( 'muted', '' );
    video.loop = true;
    video.setAttribute( 'loop', '' );
        
    if ( window.innerWidth >= this.breakpoint ) {
      video.pause();
      video.removeAttribute( 'autoplay' );
            
      container.addEventListener( 'mouseenter', () => {
        video.play().catch( e => console.log( 'Video play failed:', e ) );
      } );
            
      container.addEventListener( 'mouseleave', () => {
        video.pause();
        video.currentTime = 0;
      } );
    } else {
      // For mobile view, use Intersection Observer
      this.setupIntersectionObserver( video, container );
    }
  }

  setupIntersectionObserver( video, container ) {
    const observer = new IntersectionObserver( ( entries ) => {
      entries.forEach( entry => {
        if ( entry.isIntersecting ) {
          // Video is in viewport
          video.play().catch( e => console.log( 'Video autoplay failed:', e ) );
        } else {
          // Video is out of viewport
          video.pause();
          video.currentTime = 0;
        }
      } );
    }, {
      root: null,
      rootMargin: '-30% 0px -50%',
      threshold: 0
    } );

    observer.observe( container );
  }

  setupIframeVideo( iframe, container ) {
    const src = iframe.src;
        
    if ( src.includes( 'youtube.com' ) || src.includes( 'youtu.be' ) ) {
      this.setupYouTubeVideo( iframe, container );
    } else if ( src.includes( 'vimeo.com' ) ) {
      this.setupVimeoVideo( iframe, container );
    } else if ( src.includes( 'wistia.com' ) || src.includes( 'wistia.net' ) ) {
      this.setupWistiaVideo( iframe, container );
    }
  }

  setupYouTubeVideo( iframe, container ) {
    const src = iframe.src;
    const videoId = this.extractYouTubeId( src );
        
    if ( !videoId ) return;
        
    const params = new URLSearchParams();
    params.set( 'mute', '1' );
    params.set( 'controls', '0' );
    params.set( 'modestbranding', '1' );
    params.set( 'rel', '0' );
    params.set( 'loop', '1' );
    params.set( 'playlist', videoId );
        
    if ( window.innerWidth >= this.breakpoint ) {
      params.set( 'autoplay', '0' );
            
      container.addEventListener( 'mouseenter', () => {
        this.postMessageToYouTube( iframe, 'playVideo' );
      } );
            
      container.addEventListener( 'mouseleave', () => {
        this.postMessageToYouTube( iframe, 'pauseVideo' );
        this.postMessageToYouTube( iframe, 'seekTo', 0 );
      } );
    } else {
      // For mobile view, use Intersection Observer
      this.setupIframeIntersectionObserver( iframe, container, 'youtube' );
    }
        
    const newSrc = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    if ( iframe.src !== newSrc ) {
      iframe.src = newSrc;
    }
  }

  setupVimeoVideo( iframe, container ) {
    const src = iframe.src;
    const url = new URL( src );
        
    url.searchParams.set( 'muted', '1' );
    url.searchParams.set( 'controls', '0' );
    url.searchParams.set( 'title', '0' );
    url.searchParams.set( 'byline', '0' );
    url.searchParams.set( 'portrait', '0' );
    url.searchParams.set( 'loop', '1' );
        
    if ( window.innerWidth >= this.breakpoint ) {
      url.searchParams.set( 'autoplay', '0' );
            
      container.addEventListener( 'mouseenter', () => {
        this.postMessageToVimeo( iframe, 'play' );
      } );
            
      container.addEventListener( 'mouseleave', () => {
        this.postMessageToVimeo( iframe, 'pause' );
        this.postMessageToVimeo( iframe, 'setCurrentTime', 0 );
      } );
    } else {
      // For mobile view, use Intersection Observer
      this.setupIframeIntersectionObserver( iframe, container, 'vimeo' );
    }
        
    if ( iframe.src !== url.toString() ) {
      iframe.src = url.toString();
    }
  }

  setupWistiaVideo( iframe, container ) {
    const src = iframe.src;
    const url = new URL( src );
        
    url.searchParams.set( 'muted', 'true' );
    url.searchParams.set( 'controlsVisibleOnLoad', 'false' );
    url.searchParams.set( 'playerColor', 'ffffff' );
    url.searchParams.set( 'endVideoBehavior', 'loop' );
        
    if ( window.innerWidth >= this.breakpoint ) {
      url.searchParams.set( 'autoPlay', 'false' );
            
      container.addEventListener( 'mouseenter', () => {
        this.postMessageToWistia( iframe, 'play' );
      } );
            
      container.addEventListener( 'mouseleave', () => {
        this.postMessageToWistia( iframe, 'pause' );
        this.postMessageToWistia( iframe, 'time', 0 );
      } );
    } else {
      // For mobile view, use Intersection Observer
      this.setupIframeIntersectionObserver( iframe, container, 'wistia' );
    }
        
    if ( iframe.src !== url.toString() ) {
      iframe.src = url.toString();
    }
  }

  setupIframeIntersectionObserver( iframe, container, type ) {
    const observer = new IntersectionObserver( ( entries ) => {
      entries.forEach( entry => {
        if ( entry.isIntersecting ) {
          // Video is in viewport
          switch ( type ) {
          case 'youtube':
            this.postMessageToYouTube( iframe, 'playVideo' );
            break;
          case 'vimeo':
            this.postMessageToVimeo( iframe, 'play' );
            break;
          case 'wistia':
            this.postMessageToWistia( iframe, 'play' );
            break;
          }
        } else {
          // Video is out of viewport
          switch ( type ) {
          case 'youtube':
            this.postMessageToYouTube( iframe, 'pauseVideo' );
            this.postMessageToYouTube( iframe, 'seekTo', 0 );
            break;
          case 'vimeo':
            this.postMessageToVimeo( iframe, 'pause' );
            this.postMessageToVimeo( iframe, 'setCurrentTime', 0 );
            break;
          case 'wistia':
            this.postMessageToWistia( iframe, 'pause' );
            this.postMessageToWistia( iframe, 'time', 0 );
            break;
          }
        }
      } );
    }, {
      root: null,
      rootMargin: '-30% 0px -50%',
      threshold: 0
    } );

    observer.observe( container );
  }

  extractYouTubeId( url ) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match( regExp );
    return ( match && match[2].length === 11 ) ? match[2] : null;
  }

  postMessageToYouTube( iframe, command, value = null ) {
    const message = {
      event: 'command',
      func: command
    };
        
    if ( value !== null ) {
      message.args = [value];
    }
        
    iframe.contentWindow.postMessage( JSON.stringify( message ), 'https://www.youtube.com' );
  }

  postMessageToVimeo( iframe, method, value = null ) {
    const message = {
      method: method
    };
        
    if ( value !== null ) {
      message.value = value;
    }
        
    iframe.contentWindow.postMessage( JSON.stringify( message ), 'https://player.vimeo.com' );
  }

  postMessageToWistia( iframe, command, value = null ) {
    const message = {
      method: command
    };
        
    if ( value !== null ) {
      message.value = value;
    }
        
    iframe.contentWindow.postMessage( JSON.stringify( message ), 'https://fast.wistia.net' );
  }

  handleResize() {
    this.setupVideoControls();
  }
}

( function initializeVideoController() {
  if ( document.readyState === 'loading' ) {
    document.addEventListener( 'DOMContentLoaded', () => {
      VideoImageCardController.initialize();
    } );
  } else {
    VideoImageCardController.initialize();
  }
} )();
